
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useProjectResults } from "@/utils/use-project-results";
import { formatNumber } from '@/utils/format';

export default function ProjectDebt() {
  const { id } = useParams();
  const { results, loading, error } = useProjectResults(id);

  const { t } = useTranslation();

  if (loading) return <div className="p-4 text-xs">{t("common.loading")}</div>;
  if (error || !results) return <div className="p-4 text-elani-orange text-xs">{t("common.error_loading_data")}</div>;

  const hasStructuringCosts = (results.structuring_costs ?? []).some(
    (row) => (row["Structuring Costs (EUR)"] ?? 0) < 0
  );

  return (
    <div className="overflow-x-auto max-w-6xl text-xs">
      <div className="bg-white rounded-xl shadow-sm border border-border p-6">
        <div className="mb-6">
          <h3 className="text-base font-semibold mb-2">
            {t("project.debt.schedule")}
          </h3>
          <p className="text-xs text-gray-500">{t("project.debt.all_values_in_eur")}</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-xs">
            <thead className="bg-gray-200">
              <tr>
                <th className='text-left font-semibold px-2 py-2' style={{ width: '4rem' }}>{t("project.debt.columns.year")}</th>
                <th className='text-left font-semibold px-2 py-2' style={{ width: 'calc((100% - 4rem) / 4)' }}>{t("project.debt.columns.interest")}</th>
                <th className='text-left font-semibold px-2 py-2' style={{ width: 'calc((100% - 4rem) / 4)' }}>{t("project.debt.columns.principal")}</th>
                <th className='text-left font-semibold px-2 py-2' style={{ width: 'calc((100% - 4rem) / 4)' }}>
                  {t("project.debt.columns.debt_service")}
                  {hasStructuringCosts && <sup className="text-gray-500">*</sup>}
                </th>
                <th className='text-left font-semibold px-2 py-2' style={{ width: 'calc((100% - 4rem) / 4)' }}>{t("project.debt.columns.ending_balance")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {results.revenue.map((_, index) => (
                <tr key={index} className="hover:bg-gray-100 transition-colors">
                  <td className="px-2 py-1">{results.interest[index].Year}</td>
                  <td className="px-2 py-1 text-right">
                    <span className="px-1 py-0.5 rounded">
                      {formatNumber(results.interest[index]["Interest (EUR)"], 0)}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <span className="px-1 py-0.5 rounded">
                      {formatNumber(results.redemption[index]["Redemption (EUR)"], 0)}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <span className="px-1 py-0.5 rounded">
                      {formatNumber(results.debt_service[index]["Debt Service (EUR)"], 0)}
                    </span>
                  </td>
                  <td className="px-2 py-1 text-right">
                    <span className="px-1 py-0.5 rounded">
                      {formatNumber(results.remaining_debt?.[index]?.["Remaining Debt (EUR)"] ?? 0, 0)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {hasStructuringCosts && (
        <p className="mt-2 text-[10px] text-gray-600">
          {t("project.debt.structuring_costs_note")}
        </p>
      )}
    </div>
  );

}
